import { Link } from 'react-router-dom';
import type { PublicTeamMember } from '../lib/types';
import { contactFor } from '../lib/inspectorContacts';

interface Props {
  member: PublicTeamMember;
  /** Team & Support members show their role instead of inspector contact/booking. */
  support?: boolean;
}

// Uniform team card. Every inspector looks the same: photo, name, business name,
// email, phone, booking. Clicking the photo/name opens the full profile (bio,
// languages, reviews, Instagram). Support members show their role only.
export default function TeamCard({ member, support }: Props) {
  const name = member.name ?? `${member.firstName ?? ''} ${member.lastName ?? ''}`.trim();
  const initials = member.initials ?? deriveInitials(member);
  const c = contactFor(member);
  const profileHref = `/inspectors/${member.slug}`;

  return (
    <div className="team-card group flex flex-col bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200">
      <Link to={profileHref} className="block aspect-[4/5] bg-slate-100 overflow-hidden relative">
        {member.avatarUrl ? (
          <img src={member.avatarUrl} alt={name} loading="lazy"
               className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-navy-800 to-navy text-white">
            <span className="text-5xl font-extrabold tracking-tight">{initials}</span>
            <span className="mt-2 text-[10px] uppercase tracking-[0.18em] text-slate-400">Inspect Canada</span>
          </div>
        )}
      </Link>

      <div className="p-5 flex flex-col flex-1">
        <Link to={profileHref} className="font-bold text-navy text-lg leading-tight hover:text-accent transition-colors">{name}</Link>

        {support ? (
          member.title ? <p className="text-slate-500 text-sm mt-0.5">{member.title}</p> : null
        ) : (
          <>
            <p className="text-accent text-sm font-semibold mt-0.5">{c.businessName}</p>
            <div className="mt-3 space-y-1.5 text-sm">
              <a href={`mailto:${c.email}`} className="flex items-center gap-2 text-slate-600 hover:text-accent">
                <span className="text-slate-400 shrink-0">✉</span><span className="truncate">{c.email}</span>
              </a>
              <a href={`tel:${c.phone.replace(/[^0-9+]/g, '')}`} className="flex items-center gap-2 text-slate-600 hover:text-accent">
                <span className="text-slate-400 shrink-0">✆</span><span>{c.phone}</span>
              </a>
            </div>
            <a href={c.bookingUrl} target="_blank" rel="noreferrer"
               className="mt-4 block text-center bg-accent hover:bg-accent-dark text-white font-semibold text-sm py-2.5 rounded-xl transition-colors">
              Book an inspection
            </a>
          </>
        )}

        <Link to={profileHref} className="mt-3 text-accent font-semibold text-xs inline-flex items-center gap-1 hover:gap-2 transition-all">
          View full profile →
        </Link>
      </div>
    </div>
  );
}

function deriveInitials(m: PublicTeamMember): string {
  const f = (m.firstName?.[0] ?? '').toUpperCase();
  const l = (m.lastName?.[0] ?? '').toUpperCase();
  return `${f}${l}` || '·';
}
